import { useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { GlobalEvents } from '@highlight/ipc'
import { getChatApiRoute } from '../api/v1Client'
import { useAuth } from '../providers'
import { useLdFlag } from './useLdFlag'
import type { Skill, SkillModel, SkillTriggerEvents, SkillTriggerEventScopes, SkillType } from '../types'
import { fetchWithAuth } from './skills-api'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'

export { fetchWithAuth } from './skills-api'

/** Backend tags internal-catalog defaults with this `catalog` value. */
const INTERNAL_SKILL_CATALOG = 'internal'

export const SKILLS_QUERY_KEY = 'skills'
export const DEFAULT_SKILLS_QUERY_KEY = 'default-skills'
const SKILL_EVENT_CATALOG_QUERY_KEY = 'skill-event-catalog'
const SKILL_MODELS_QUERY_KEY = 'skill-models'

// Cross-window signal fired whenever the skill set changes (create, update,
// delete, or fork). The listener lives at ReactQueryProvider so other
// windows invalidate even when no useSkills consumer is mounted — see
// chat/providers/react-query-provider.tsx.
export const SKILLS_CHANGED_EVENT = 'skills:changed'

export const useSkills = () => {
  const { getAuthToken } = useAuth()

  return useQuery({
    queryKey: [SKILLS_QUERY_KEY],
    queryFn: async (): Promise<Array<Skill>> => {
      try {
        const token = await getAuthToken()
        const json = await fetchWithAuth(getChatApiRoute('api/v1/skills'), token)
        return (json.data ?? []) as Array<Skill>
      } catch (error) {
        console.error('Error fetching user skills:', error)
        throw error
      }
    },
    staleTime: 1000 * 60 * 10,
  })
}

export const useDefaultSkills = () => {
  const { getAuthToken } = useAuth()
  const showInternalSkills = useLdFlag<boolean>('display-internal-skills', false)

  const query = useQuery({
    queryKey: [DEFAULT_SKILLS_QUERY_KEY],
    queryFn: async (): Promise<Array<Skill>> => {
      try {
        const token = await getAuthToken()
        const json = await fetchWithAuth(getChatApiRoute('api/v1/skills?defaults=true'), token)
        return (json.data ?? []) as Array<Skill>
      } catch (error) {
        console.error('Error fetching default skills:', error)
        throw error
      }
    },
    staleTime: 1000 * 60 * 30,
  })

  const data = useMemo(() => {
    if (!query.data || showInternalSkills) {
      return query.data
    }
    return query.data.filter((skill) => skill.catalog !== INTERNAL_SKILL_CATALOG)
  }, [query.data, showInternalSkills])

  return { ...query, data }
}

/** Enabled user skills plus unforked defaults. A fork, even a disable-fork, stands in for its original. */
export const useAvailableSkills = (): Array<Skill> => {
  const { data: userSkills } = useSkills()
  const { data: defaultSkills } = useDefaultSkills()
  return useMemo(() => {
    const user = userSkills ?? []
    const defaults = defaultSkills ?? []
    const forkedFromIds = new Set(user.map((s) => s.forkedFromId).filter((id): id is string => id != null))
    const unforkedDefaults = defaults.filter((d) => !forkedFromIds.has(d.id))
    const seen = new Set<string>()
    return [...user, ...unforkedDefaults].filter((skill) => {
      if (skill.disabledForUser || seen.has(skill.id)) return false
      seen.add(skill.id)
      return true
    })
  }, [userSkills, defaultSkills])
}

export const useSkillById = (id?: string) => {
  const { getAuthToken } = useAuth()

  return useQuery({
    queryKey: [SKILLS_QUERY_KEY, id],
    queryFn: async (): Promise<Skill | null> => {
      try {
        const token = await getAuthToken()
        const json = await fetchWithAuth(getChatApiRoute(`api/v1/skills/${id}`), token)
        return (json.data ?? null) as Skill | null
      } catch (error) {
        // The editor can still fall back to the skill data passed in modal context.
        console.error('Error fetching skill by id:', error)
        return null
      }
    },
    enabled: !!id,
  })
}

interface CreateSkillInput {
  name: string
  description?: string
  prompt?: string
  skillContent?: string
  toolset?: string
  skillType?: SkillType
  examples?: { good?: Array<string>; bad?: Array<string> }
  metadata?: Record<string, unknown>
  public?: boolean
  // Initial per-surface availability. Omitted keys default to the backend
  // default (true). Used at creation to opt new skills into "Suggest in
  // chat" but not "Run after meetings".
  availability?: { audioPlanner?: boolean; agents?: boolean; userInvoke?: boolean }
  folder?: string
  autoMode?: boolean
}

export const useCreateSkill = () => {
  const { getAuthToken } = useAuth()

  return useMutation({
    mutationKey: ['create-skill'],
    mutationFn: async (input: CreateSkillInput): Promise<Skill> => {
      try {
        const token = await getAuthToken()
        const json = await fetchWithAuth(getChatApiRoute('api/v1/skills'), token, {
          method: 'POST',
          body: JSON.stringify(input),
        })
        return json.data as Skill
      } catch (error) {
        console.error('Error creating skill:', error)
        throw error
      }
    },
    onSuccess: (skill) => {
      AmplitudeRenderer.trackEvent('Skill Action', { action: 'create', skillId: skill?.id })
      GlobalEvents.emit(SKILLS_CHANGED_EVENT)
    },
  })
}

export interface UpdateSkillInput {
  id: string
  name?: string
  description?: string
  prompt?: string
  skillContent?: string | null
  /**
   * Replaces the whole preference list, in order. Unlike every other field here,
   * a preferences-only patch does NOT fork a default — send it against the
   * default's own id. `source` is any slug: `user`, `agent`, or one a skill
   * picked for an entry it maintains — round-trip it rather than relabelling.
   * `id` round-trips too: carry it and the entry keeps its creation date and
   * provenance, omit it and the entry is stored as newly written by the user.
   */
  preferences?: Array<{ id?: string; content: string; source?: string }> | null
  toolset?: string | null
  skillType?: SkillType
  examples?: { good?: Array<string>; bad?: Array<string> } | null
  metadata?: Record<string, unknown> | null
  public?: boolean
  // Partial per-surface availability override — omitted keys keep their
  // current value (the backend merges).
  availability?: { audioPlanner?: boolean; agents?: boolean; userInvoke?: boolean }
  schedule?: import('@renderer/chat/types').SkillSchedule | null
  disabled?: boolean
  folder?: string
  /**
   * App-event trigger configuration. Pass an object to use a frozen manual
   * list (`{}` turns app events off). Pass `null` to enable auto-detection.
   */
  triggerEvents?: SkillTriggerEvents | null
  /**
   * Relevance scopes parallel to a manual `triggerEvents` override. Send this
   * alongside any non-null `triggerEvents` to preserve the auto-detected
   * scopes — the backend resets scopes to all-`any` when an override omits
   * them. Omit (or send with `triggerEvents: null`) to let auto-detection set
   * scopes on the next content change.
   */
  triggerEventScopes?: SkillTriggerEventScopes | null
  /** Lets webhook-triggered runs approve their own trigger-app actions. See `Skill.autoMode`. */
  autoMode?: boolean
  /** Null switches the skill back to Auto. See `Skill.modelId`. */
  modelId?: string | null
}

export const useUpdateSkill = () => {
  const { getAuthToken } = useAuth()

  return useMutation({
    mutationKey: ['update-skill'],
    mutationFn: async ({ id, ...updates }: UpdateSkillInput): Promise<Skill> => {
      try {
        const token = await getAuthToken()
        const json = await fetchWithAuth(getChatApiRoute(`api/v1/skills/${id}`), token, {
          method: 'PATCH',
          body: JSON.stringify(updates),
        })
        return json.data as Skill
      } catch (error) {
        console.error('Error updating skill:', error)
        throw error
      }
    },
    onSuccess: () => {
      GlobalEvents.emit(SKILLS_CHANGED_EVENT)
    },
  })
}

export const useDeleteSkill = () => {
  const { getAuthToken } = useAuth()

  return useMutation({
    mutationKey: ['delete-skill'],
    mutationFn: async (id: string): Promise<void> => {
      try {
        const token = await getAuthToken()
        await fetchWithAuth(getChatApiRoute(`api/v1/skills/${id}`), token, {
          method: 'DELETE',
        })
      } catch (error) {
        console.error('Error deleting skill:', error)
        throw error
      }
    },
    onSuccess: () => {
      GlobalEvents.emit(SKILLS_CHANGED_EVENT)
    },
  })
}

export const useForkSkill = () => {
  const { getAuthToken } = useAuth()

  return useMutation({
    mutationKey: ['fork-skill'],
    mutationFn: async (id: string): Promise<Skill> => {
      try {
        const token = await getAuthToken()
        const json = await fetchWithAuth(getChatApiRoute(`api/v1/skills/${id}/fork`), token, {
          method: 'POST',
        })
        return json.data as Skill
      } catch (error) {
        console.error('Error forking skill:', error)
        throw error
      }
    },
    onSuccess: (skill, forkedFromId) => {
      AmplitudeRenderer.trackEvent('Skill Action', { action: 'fork', skillId: skill?.id, forkedFromId })
      GlobalEvents.emit(SKILLS_CHANGED_EVENT)
    },
  })
}

interface PublishSkillInput {
  id: string
  organizationId: string
}

export const usePublishSkill = () => {
  const { getAuthToken } = useAuth()

  return useMutation({
    mutationKey: ['publish-skill'],
    mutationFn: async ({ id, organizationId }: PublishSkillInput): Promise<Skill> => {
      try {
        const token = await getAuthToken()
        const json = await fetchWithAuth(getChatApiRoute(`api/v1/skills/${id}/publish`), token, {
          method: 'POST',
          body: JSON.stringify({ organizationId }),
        })
        return json.data as Skill
      } catch (error) {
        console.error('Error publishing skill:', error)
        throw error
      }
    },
    onSuccess: () => {
      GlobalEvents.emit(SKILLS_CHANGED_EVENT)
    },
  })
}

export const useUnpublishSkill = () => {
  const { getAuthToken } = useAuth()

  return useMutation({
    mutationKey: ['unpublish-skill'],
    mutationFn: async ({ id, organizationId }: PublishSkillInput): Promise<void> => {
      try {
        const token = await getAuthToken()
        await fetchWithAuth(getChatApiRoute(`api/v1/organizations/${organizationId}/skills/${id}`), token, {
          method: 'DELETE',
        })
      } catch (error) {
        console.error('Error unpublishing skill:', error)
        throw error
      }
    },
    onSuccess: () => {
      GlobalEvents.emit(SKILLS_CHANGED_EVENT)
    },
  })
}

/**
 * One catalog entry — the friendly label/description the backend surfaces for
 * a single webhook event type a skill can subscribe to via `triggerEvents`.
 */
export interface SkillEventCatalogEvent {
  type: string
  label: string
  description: string
}

export interface SkillEventCatalogProvider {
  provider: string
  displayName: string
  events: Array<SkillEventCatalogEvent>
}

/**
 * Fetches the webhook event catalog skills can subscribe to. The catalog is
 * provider-grouped and carries UI-ready labels — the same `type` strings
 * persisted in `skill.triggerEvents.<provider>` — so the UI can render
 * detected triggers without hardcoding provider names.
 */
export const useSkillEventCatalog = (options?: { enabled?: boolean }) => {
  const { getAuthToken } = useAuth()

  return useQuery({
    queryKey: [SKILL_EVENT_CATALOG_QUERY_KEY],
    queryFn: async (): Promise<Array<SkillEventCatalogProvider>> => {
      const token = await getAuthToken()
      const json = await fetchWithAuth(getChatApiRoute('api/v1/skills/event-catalog'), token)
      return (json.data ?? []) as Array<SkillEventCatalogProvider>
    },
    staleTime: 1000 * 60 * 60,
    enabled: options?.enabled ?? true,
  })
}

/** The server-owned list of models a skill can be pinned to. Auto is not in it. */
export const useSkillModels = () => {
  const { getAuthToken } = useAuth()

  return useQuery({
    queryKey: [SKILL_MODELS_QUERY_KEY],
    queryFn: async (): Promise<Array<SkillModel>> => {
      const token = await getAuthToken()
      const json = await fetchWithAuth(getChatApiRoute('api/v1/skills/models'), token)
      return (json.data?.models ?? []) as Array<SkillModel>
    },
    staleTime: 1000 * 60 * 60,
  })
}

export interface PreviewSkillTriggersInput {
  name?: string
  description?: string
  skillContent?: string
}

export interface PreviewSkillTriggersResult {
  triggerEvents: SkillTriggerEvents
  /** Auto-detected relevance scopes parallel to `triggerEvents`. */
  triggerEventScopes: SkillTriggerEventScopes
}

/**
 * Dry-runs the skill trigger auto-detection LLM against draft text without
 * saving. Drives the ghost chips in auto mode and the opt-in nudge while app
 * events are off. Callers debounce draft changes.
 */
export const usePreviewSkillTriggers = () => {
  const { getAuthToken } = useAuth()

  return useMutation({
    mutationKey: ['preview-skill-triggers'],
    mutationFn: async (input: PreviewSkillTriggersInput): Promise<PreviewSkillTriggersResult> => {
      const token = await getAuthToken()
      const json = await fetchWithAuth(getChatApiRoute('api/v1/skills/preview-triggers'), token, {
        method: 'POST',
        body: JSON.stringify(input),
      })
      return (json.data ?? { triggerEvents: {}, triggerEventScopes: {} }) as PreviewSkillTriggersResult
    },
  })
}

export interface SkillCostProviderBreakdown {
  provider: string
  displayName: string
  connected: boolean
  eventCount: number
  runCount: number
}

/**
 * Where the price per run came from. `sampled`: Jev judged a sample of the
 * user's recent bursts against the saved skill. `fleet`: the all-skills average.
 */
export interface SkillCostSpawnProfile {
  basis: 'sampled' | 'fleet'
  /** Share of runs that would start a chat. */
  spawnShare: number
  /** Share of started chats on Auto's advanced model. */
  advancedShare: number
  sampledRuns: number
}

export interface SkillCostEstimate {
  windowDays: number
  eventCount: number
  runCount: number
  projectedEventsPerMonth: number
  projectedRunsPerMonth: number
  /** Highlight credits (1 credit = $0.01). */
  costPerRunCredits: number
  estimatedCreditsPerMonth: number
  rangeCredits: { low: number; high: number }
  spawnProfile: SkillCostSpawnProfile
  confidence: 'none' | 'low' | 'medium' | 'high'
  hasMatchingConnection: boolean
  perProvider: Array<SkillCostProviderBreakdown>
  /** `false` when the user isn't flagged into webhook-triggered skills. */
  estimateRan: boolean
}

const EMPTY_COST_ESTIMATE: SkillCostEstimate = {
  windowDays: 14,
  eventCount: 0,
  runCount: 0,
  projectedEventsPerMonth: 0,
  projectedRunsPerMonth: 0,
  costPerRunCredits: 0,
  estimatedCreditsPerMonth: 0,
  rangeCredits: { low: 0, high: 0 },
  spawnProfile: { basis: 'fleet', spawnShare: 0, advancedShare: 0, sampledRuns: 0 },
  confidence: 'none',
  hasMatchingConnection: false,
  perProvider: [],
  estimateRan: false,
}

function hasAnyTriggers(triggerEvents: SkillTriggerEvents): boolean {
  return Object.values(triggerEvents ?? {}).some((v) => Array.isArray(v) && v.length > 0)
}

/**
 * Predicts the monthly cost of a webhook-triggered skill from the user's
 * recent webhook activity. The backend replays the last N days of matching
 * events through the same per-entity debounce the live pipeline uses (so it
 * counts runs, not raw events) and prices each run. Keyed on the serialized
 * trigger config so it re-estimates whenever the user changes their picks;
 * disabled (no request) until at least one event is selected.
 *
 * Pass `triggerEventScopes` so the replay applies the same owner-scope gate as
 * the live pipeline — without it the backend over-counts (e.g. an "assigned to
 * me" skill is priced as if it ran on every workspace issue).
 *
 * Pass the saved `skill` so the backend can have Jev judge a sample of the
 * user's runs against it. Its saved text and model are in the key because the
 * trigger spec Jev judges on is compiled from them on save.
 */
export const useEstimateSkillCost = (
  triggerEvents: SkillTriggerEvents,
  triggerEventScopes: SkillTriggerEventScopes = {},
  options?: {
    enabled?: boolean
    skill?: Pick<Skill, 'id' | 'name' | 'description' | 'skillContent' | 'modelId'>
  },
) => {
  const { getAuthToken } = useAuth()
  const enabled = (options?.enabled ?? true) && hasAnyTriggers(triggerEvents)
  const skill = options?.skill

  return useQuery({
    queryKey: [
      'estimate-skill-cost',
      JSON.stringify(triggerEvents),
      JSON.stringify(triggerEventScopes),
      skill?.id ?? null,
      skill ? [skill.name, skill.description, skill.skillContent, skill.modelId ?? null] : null,
    ],
    queryFn: async (): Promise<SkillCostEstimate> => {
      const token = await getAuthToken()
      const json = await fetchWithAuth(getChatApiRoute('api/v1/skills/cost-estimate'), token, {
        method: 'POST',
        body: JSON.stringify({ triggerEvents, triggerEventScopes, skillId: skill?.id }),
      })
      return (json.data ?? EMPTY_COST_ESTIMATE) as SkillCostEstimate
    },
    enabled,
    staleTime: 1000 * 60 * 5,
    // Keep the last estimate while re-estimating the same skill, never another skill's.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[3] === (skill?.id ?? null) ? previous : undefined,
  })
}

export interface ResetSkillsOptions {
  /** Also unsubscribe from every skill shared with you. Off by default. */
  includeTeamSkills?: boolean
  /** Also unpublish and delete the skills you shared with your team. Off by default. */
  includeSharedSkills?: boolean
}

export const useResetSkills = () => {
  const { getAuthToken } = useAuth()

  return useMutation({
    mutationKey: ['reset-skills'],
    mutationFn: async (options: ResetSkillsOptions = {}): Promise<void> => {
      try {
        const token = await getAuthToken()
        await fetchWithAuth(getChatApiRoute('api/v1/skills/reset'), token, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            includeTeamSkills: options.includeTeamSkills ?? false,
            includeSharedSkills: options.includeSharedSkills ?? false,
          }),
        })
      } catch (error) {
        console.error('Error resetting skills:', error)
        throw error
      }
    },
    onSuccess: (_, options) => {
      AmplitudeRenderer.trackEvent('Skill Action', {
        action: 'reset all',
        includeTeamSkills: options?.includeTeamSkills ?? false,
        includeSharedSkills: options?.includeSharedSkills ?? false,
      })
      GlobalEvents.emit(SKILLS_CHANGED_EVENT)
    },
  })
}
